import { useMemo } from 'react';

import { useAppSelector } from '@verantos-dev/app-state-base';
import {
  selectCohortDefinition,
  selectConceptGroups,
} from '@verantos-dev/app-state-cohort-builder';

import { getConceptFromDifferentFilterTypes } from '../../utils/concept-group-utils';

export type ConceptDetail = {
  id: string;
  attribute: string;
  name: string;
  isExist: boolean;
};

/**
 * Hook to retrieve all concept groups from the cohort definition.
 *
 * This hook extracts concept groups from different parts of the cohort definition,
 * including simple events, related entity filters, and subject variables. It then
 * deduplicates the concepts to ensure there are no duplicates in the final list.
 */
export const useGetAllConceptGroupFromDefinition = () => {
  const cohortDefinition = useAppSelector(selectCohortDefinition);
  const conceptList = useAppSelector(selectConceptGroups);

  const conceptGroups = useMemo(
    () =>
      cohortDefinition?.simpleEvents
        .filter((criterion) => criterion.conceptFilters.length > 0)
        .map((criterion) => criterion.conceptFilters)
        .reduce((acc: ConceptDetail[], conceptFilters) => {
          const concepts = getConceptFromDifferentFilterTypes(
            acc,
            conceptFilters,
            0,
            2,
            conceptList
          );
          return acc.concat(concepts);
        }, []) || [],
    [cohortDefinition?.simpleEvents, conceptList]
  );

  const relatedConcepts = useMemo(
    () =>
      cohortDefinition?.simpleEvents
        .filter((criterion) => criterion.relatedEntityFilters.length > 0)
        .map((criterion) => criterion.relatedEntityFilters)
        .reduce((acc: ConceptDetail[], relatedEntityFilters) => {
          const concepts = getConceptFromDifferentFilterTypes(
            acc,
            relatedEntityFilters,
            1,
            3,
            conceptList
          );
          return acc.concat(concepts);
        }, []) || [],
    [cohortDefinition?.simpleEvents, conceptList]
  );

  if (relatedConcepts.length > 0) {
    conceptGroups.push(...relatedConcepts);
  }

  const conceptGroupsSubjectVariable = useMemo(
    () =>
      cohortDefinition?.subjectVariables
        .filter((criterion) => criterion.conceptFilters.length > 0)
        .map((criterion) => criterion.conceptFilters)
        .reduce((acc: ConceptDetail[], conceptFilters) => {
          const concepts = getConceptFromDifferentFilterTypes(
            acc,
            conceptFilters,
            0,
            2,
            conceptList
          );
          return acc.concat(concepts);
        }, []) || [],
    [cohortDefinition?.subjectVariables, conceptList]
  );

  if (conceptGroupsSubjectVariable.length > 0) {
    conceptGroups.push(...conceptGroupsSubjectVariable);
  }

  // Deduplicate relatedConcepts
  const dedupedConcepts = Array.from(
    new Set(conceptGroups.map((concept) => JSON.stringify(concept)))
  ).map((concept) => JSON.parse(concept));

  return { conceptGroups: dedupedConcepts };
};
